<template>
  <v-container class="fill-height" fluid :style="containerStyle">
    <!-- 리워드 달성 축하 팝업 (브라우저 높이 680px 이하일 때) -->
    <v-snackbar
      v-model="rewardDialog"
      :timeout="-1"
      top
      color="primary"
      elevation="24"
      min-width="320px"
    >
      <div class="text-center py-2">
        <div style="font-size: 1.2rem; font-weight: 600;">
          🎉 10회 클릭 완료! 🎉
        </div>
        <div style="font-size: 1rem; font-weight: 500; margin-top: 8px;">
          총 {{ totalClickReward.toFixed(1) }} UMCP 획득
        </div>
        <div style="font-size: 0.9rem; margin-top: 4px; opacity: 0.9;">
          리워드를 수령하세요!
        </div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="onRewardClick"
          large
        >
          수령하기
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Web3 지갑 연결 모달 -->
    <v-dialog v-model="dialog" persistent max-width="500">
      <v-card>
        <v-card-title class="headline">{{ $t('eventGame.walletConnect') }}</v-card-title>
        <v-card-text>
          {{ $t('eventGame.selectWallet') }}
          <div class="mt-4">
            <v-btn
              color="#ee4b0bff"
              block
              @click="connectWallet('metamask')"
            >
              MetaMask
            </v-btn>
            <v-btn
              color="purple"
              block
              class="mt-2"
              @click="connectWallet('phantom')"
            >
              Phantom
            </v-btn>
            <v-btn
              color="blue"
              block
              class="mt-2"
              @click="connectWallet('tokenpocket')"
            >
              TokenPocket
            </v-btn>
            <!-- Manual wallet address input -->
            <v-text-field
              v-model="manualAddress"
              :label="$t('eventGame.manualAddress')"
              :placeholder="$t('eventGame.manualAddressPlaceholder')"
              dense
              outlined
              class="mt-3"
            ></v-text-field>

            <v-btn
              block
              class="mt-2" 
              color="primary"
              @click="connectManualAddress"
            >
              {{ $t('eventGame.useManualAddress') }}
            </v-btn>
          </div>
          <div v-if="account" class="mt-4" style="font-size:0.9rem; color:#4caf50;">
            {{ $t('eventGame.currentAddress', { address: account }) }}
          </div>
          <div v-if="errorMessage" class="mt-2" style="font-size:0.85rem; color:#e53935;">
            {{ errorMessage }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="onReject">{{ $t('eventGame.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 메인 게임 화면 -->
    <v-row align="center" justify="center">
      <v-col cols="12" md="6" class="text-center">
        <!-- 진행 바: 서명 후 다음 보상 티어까지 남은 클릭수 표시 -->
        <div v-if="lastSignature" class="mt-3">
          <v-card class="pa-3" outlined>
            <div style="display:flex; align-items:center; justify-content:space-between;">
              <div style="font-size:0.95rem; font-weight:500;">
                {{ $t('eventGame.remaining', { remaining: remainingClicks, threshold: nextThreshold || '-' }) }}
              </div>
              <div style="font-size:0.85rem; color:#666;">{{ progressPercent }}%</div>
            </div>
            <v-progress-linear :value="progressPercent" height="10" color="primary" class="mt-2"></v-progress-linear>
            <!-- Reward points display area -->
            <div class="mt-3" style="font-size:0.9rem; color:#333;">
              <template v-if="rewardLoading">
                {{ $t('eventGame.loadingReward') }}
              </template>
              <template v-else>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      style="display:inline-block;"
                    >
                      {{ $t('eventGame.totalReward') }}: <strong>{{ rewardTotal }}</strong> UMCP
                    </div>
                  </template>
                  <span>{{ $t('eventGame.tooltipReward', { out: rewardOut, late: rewardLate }) }}</span>
                </v-tooltip>
                <!-- 
                <div style="font-size:0.85rem; color:#666;">
                  사용(출금): {{ rewardOut }} / 전환가능: {{ rewardLate }}
                </div>
                -->
              </template>
            </div>
          </v-card>
        </div>

        <!-- 중앙 이미지 클릭 영역 (2장 전환) -->
        <div style="position: relative;">
          <v-img
            :src="images[currentIndex]"
            contain
            :height="imageHeight"
            class="mt-4"
            @click="onClickImage($event)"
            style="cursor:pointer; border-radius:16px;"
          ></v-img>
          
          <!-- 동전 터지는 효과 -->
          <div
            v-for="coin in coins"
            :key="coin.id"
            class="coin-particle"
            :style="{
              left: coin.x + 'px',
              top: coin.y + 'px',
              transform: `translate(${coin.vx}px, ${coin.vy}px)`,
              opacity: coin.opacity
            }"
          >
            💰
          </div>
        </div>

        <!-- 게임 참여 영역: 기본적으로 이미지 아래에 숨겨둠 -->
        <div class="game-actions mt-4">
          <!-- Button shown when no wallet and not logged in -->
          <v-btn
              v-if="!account && !isLogin"
              color="primary"
              block
              @click="showWalletDialog"
            >
              {{ $t('eventGame.walletConnectLogin') }}
            </v-btn>

          <!-- Game and signature area shown when wallet is connected -->
          <div v-else>
            <v-card class="pa-4" outlined>
              <div v-if="account" style="font-size:0.9rem; margin-top:4px;">{{ $t('eventGame.wallet') }}: {{ shortAccount }}</div>
              <v-divider class="my-3"></v-divider>

              <!-- Connect wallet button: shown when no provider (manual address only) -->
              <v-btn
                v-if="!provider"
                class="mt-2"
                color="primary"
                block
                @click="showWalletDialog"
              >
                {{ $t('eventGame.connectWallet') }}
              </v-btn>

              <input type="hidden" v-model="messageToSign" />

              <v-btn
                v-if="!lastSignature"
                class="mt-2"
                color="primary"
                block
                :disabled="!account || !provider"
                @click="signMessage"
              >
                {{ $t('eventGame.playGame') }}
              </v-btn>

              <div v-if="lastSignature" style="margin-top:8px; font-size:0.8rem; word-break:break-all;">
                {{ $t('eventGame.signature') }}: {{ lastSignature }}
              </div>

              <!-- Reward button shown after 10 clicks completed -->
              <v-btn
                v-if="lastSignature && count >= maxClicks"
                class="mt-2"
                color="primary"
                block
                @click="onRewardClick"
              >
                🎉 리워드 수령 ({{ totalClickReward.toFixed(1) }} UMCP)
              </v-btn>

              <!-- Retry button shown after 10 clicks completed -->
              <v-btn
                v-if="lastSignature && count >= maxClicks"
                class="mt-2"
                color="warning"
                block
                outlined
                @click="onRetryClick"
              >
                재도전 (-10 UMCP)
              </v-btn>

              <div v-if="errorMessage" style="margin-top:4px; font-size:0.8rem; color:#e53935;">
                {{ errorMessage }}
              </div>
            </v-card>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Web3 from 'web3'
import ultraTab0 from '@/assets/img/ultra_tab_0.svg'
import ultraTab1 from '@/assets/img/ultra_tab_1.svg'
import { reward_claim , rewards } from '@/api';

export default {
  data() {
    return {
      dialog: true,
      count: 0,
      images: [
        ultraTab0,
        ultraTab1
      ],
      currentIndex: 0,
      account: null,
      errorMessage: '',
      messageToSign: '게임참여',
      lastSignature: '',
      // 10클릭 게임 시스템
      maxClicks: 10,
      clickRewards: [], // 각 클릭의 리워드 저장
      totalClickReward: 0, // 누적 리워드
      _total: 0, // 게임 시작 시 결정된 총 리워드 (5.0~9.5)
      _totalAry: [], // 총 리워드를 무작위로 분리한 배열
      provider: null,
      web3: null,
      manualAddress: '',
      walletType: 'manual' // 'metamask', 'phantom', 'tokenpocket', 'manual'
      ,
      // 리워드 요약 데이터
      rewardTotal: 0,
      rewardOut: 0,
      rewardLate: 0,
      rewardLoading: false,
      // 개발자 도구 감지
      devToolsInterval: null,
      devToolsOpen: false,
      // 개발자 도구 차단 여부 (true: 차단/리로드, false: 허용)
      devToolsBlock: false,
      // 리워드 달성 축하 모달 (680px 이하 화면에서만 사용)
      rewardDialog: false,
      // 동전 터지는 효과
      coins: [],
      coinIdCounter: 0,
    }
  },
  computed: {
    isSmallScreen() {
      return window.innerHeight <= 680
    },
    imageHeight() {
      return this.isSmallScreen ? '250px' : '300px'
    },
    containerStyle() {
      return {
        // 중앙에서 방사형(radial) 그라데이션 적용
        background: 'radial-gradient(circle, rgb(255 255 255) 0%, rgb(115 115 115) 100%)',
        minHeight: '10vh'
      }
    },
    shortAccount() {
      if (!this.account) return ''
      return this.account.slice(0, 6) + '...' + this.account.slice(-4)
    },
    isLogin() {
      try {
        return this.$store && this.$store.getters ? this.$store.getters.is_login : false
      } catch (e) {
        return false
      }
    },
    // 10클릭까지의 목표
    nextThreshold() {
      return this.maxClicks
    },
    // 남은 클릭 수
    remainingClicks() {
      return Math.max(0, this.maxClicks - this.count)
    },
    // 진행률 (10클릭 기준, 퍼센트)
    progressPercent() {
      const pct = Math.round((this.count / this.maxClicks) * 100)
      return Math.min(100, Math.max(0, pct))
    }
  },
   created() {
     // If user already logged in, don't show the wallet dialog
     if (this.$store && this.$store.getters && this.$store.getters.is_login) {
       this.dialog = false
     }
     // 개발자 도구 감지 및 차단
     this.detectDevTools()
  },
  mounted() {
    // 개발자 도구 감지 시작
    this.startDevToolsDetection()
  },
  beforeDestroy() {
    // 감지 인터벌 정리
    if (this.devToolsInterval) {
      clearInterval(this.devToolsInterval)
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        try {
          const user = this.$store && this.$store.getters ? this.$store.getters.user : this.$store.state.user
          const addr = (user && (user.wallet_address || user.walletAddress || user.address)) || ''
          if (addr && addr.startsWith && addr.startsWith('0x') && addr.length === 42) {
            this.manualAddress = addr
          }
        } catch (e) {
          // ignore
        }
      }
    }
    ,
    isLogin(val) {
      // close dialog when user becomes logged in
      if (val) this.dialog = false
    }
  },
  methods: {
    // 개발자 도구 감지 (여러 방법 조합)
    detectDevTools() {
      // 차단 비활성화 시 아무 처리도 하지 않음
      if (!this.devToolsBlock) return
      // 방법 1: console.log 재정의를 통한 감지
      const element = new Image()
      Object.defineProperty(element, 'id', {
        get: () => {
          this.devToolsOpen = true
          this.handleDevToolsOpen()
        }
      })
      
      // 방법 2: 창 크기 변화 감지
      const widthThreshold = window.outerWidth - window.innerWidth > 160
      const heightThreshold = window.outerHeight - window.innerHeight > 160
      if (widthThreshold || heightThreshold) {
        this.devToolsOpen = true
        this.handleDevToolsOpen()
      }

      // 방법 3: debugger 문 실행 시간 측정
      const start = new Date()
      // eslint-disable-next-line no-debugger
      debugger
      const end = new Date()
      if (end - start > 100) {
        this.devToolsOpen = true
        this.handleDevToolsOpen()
      }
    },
    // 개발자 도구 감지 시작 (주기적 체크)
    startDevToolsDetection() {
      // 차단 비활성화 시 리스너/인터벌 등록하지 않음
      if (!this.devToolsBlock) return

      this.devToolsInterval = setInterval(() => {
        const widthThreshold = window.outerWidth - window.innerWidth > 160
        const heightThreshold = window.outerHeight - window.innerHeight > 160
        
        if (widthThreshold || heightThreshold) {
          if (!this.devToolsOpen) {
            this.devToolsOpen = true
            this.handleDevToolsOpen()
          }
        } else {
          this.devToolsOpen = false
        }
      }, 1000)

      // 우클릭 방지
      document.addEventListener('contextmenu', (e) => {
        if (!this.devToolsBlock) return
        e.preventDefault()
        return false
      })

      // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U 키 차단
      document.addEventListener('keydown', (e) => {
        if (!this.devToolsBlock) return
        if (
          e.key === 'F12' ||
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          (e.ctrlKey && e.shiftKey && e.key === 'J') ||
          (e.ctrlKey && e.shiftKey && e.key === 'C') ||
          (e.ctrlKey && e.key === 'U')
        ) {
          e.preventDefault()
          return false
        }
      })
    },
    // 개발자 도구가 열렸을 때 처리
    handleDevToolsOpen() {
      // 차단 여부에 따라 처리
      if (this.devToolsBlock) {
        // 페이지 리로드
        try {
          window.location.reload()
        } catch (e) {
          // fallback: 강제로 홈으로 이동
          window.location.href = '/'
        }
      }
    },
    // 리워드 현황
    async call_rewards( addr ){
      try{
        this.rewardLoading = true
        const res = await rewards({ wallet_address: addr })
        const data = res && res.data ? res.data : res
        // 기대 응답: { status:true, reward:{ total_p, out_p, late_p } }
        if (data && data.status && data.reward) {
          this.rewardTotal = Number(data.reward.total_p || 0)
          this.rewardOut = Number(data.reward.out_p || 0)
          this.rewardLate = Number(data.reward.late_p || 0)
        }
      } catch(e){
        console.error('call_rewards error', e)
      } finally {
        this.rewardLoading = false
      }

    },
    // 간단한 알림 헬퍼: store 토스트를 우선 사용, 없으면 alert로 폴백
    notify(msg) {
      if (this.$store && typeof this.$store.dispatch === 'function') {
        try {
          this.$store.dispatch('toast', {
            toast: true,
            toast_msg: msg,
            timeout: 2500,
          })
          return
        } catch (e) {
          // fallthrough to alert
        }
      }
      try {
        window.alert(msg)
      } catch (e) {
        // ignore
      }
    },
    onClickImage(event) {
      // 10회 클릭 완료 시 더 이상 클릭 불가
      if (this.count >= this.maxClicks) {
        this.notify('10회 클릭이 완료되었습니다. 리워드를 수령하세요!')
        return
      }

      // Require connected wallet and prior signature before allowing clicks
      if (!this.account) {
        // open wallet connect dialog if not connected
        this.showWalletDialog()
        return
      }

      if (!this.lastSignature) {
        // ask user to sign via 'Play Game' first
        try {
          this.$store.dispatch('toast', {
            toast: true,
            toast_msg: this.$t('eventGame.signFirst'),
            timeout: 2500,
          })
        } catch (e) {
          window.alert(this.$t('eventGame.signFirst'))
        }
        return
      }

      // 동전 터지는 효과 생성
      this.createCoinBurst(event)

      // 배열에서 순서대로 리워드 가져오기
      const clickReward = this._totalAry[this.count] || 0
      this.clickRewards.push(clickReward)
      this.totalClickReward += clickReward

      // increment click count
      this.count += 1

      // 리워드 획득 알림 (누적 금액 표시)
      this.notify(`+${clickReward.toFixed(1)} UMCP | 누적: ${this.totalClickReward.toFixed(1)} (${this.count}/${this.maxClicks})`)

      // 10회 클릭 완료 시
      if (this.count >= this.maxClicks) {
        // Show modal for small screens (680px or less)
        if (this.isSmallScreen) {
          this.rewardDialog = true
        } else {
          // Show toast message for larger screens
          this.notify(`🎉 10회 클릭 완료! 총 ${this.totalClickReward.toFixed(1)} UMCP 획득`)
        }
      }

      // toggle image
      this.currentIndex = this.currentIndex === 0 ? 1 : 0
    },
    // 게임 시작 시 총 리워드 생성: 5.0~9.5 사이
    generateTotalReward() {
      const min = 5.0
      const max = 9.5
      const total = min + Math.random() * (max - min)
      return Math.round(total * 10) / 10
    },
    // 총 리워드를 무작위로 분리 (배열 합계 = total)
    splitRewardRandomly(total, count) {
      const result = []
      let remaining = total
      
      // count-1개까지는 랜덤하게 분배
      for (let i = 0; i < count - 1; i++) {
        // 남은 금액과 남은 횟수를 고려하여 최소/최대 범위 설정
        const remainingCount = count - i
        const minValue = 0.1 // 최소 0.1
        const maxValue = Math.min(remaining - (remainingCount - 1) * minValue, remaining * 0.3) // 최대 30%
        
        const value = minValue + Math.random() * (maxValue - minValue)
        const rounded = Math.round(value * 10) / 10
        
        result.push(rounded)
        remaining -= rounded
        remaining = Math.round(remaining * 10) / 10 // 부동소수점 오차 보정
      }
      
      // 마지막 값은 정확히 남은 금액
      result.push(Math.round(remaining * 10) / 10)
      
      // 배열 섞기 (Fisher-Yates shuffle)
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]]
      }
      
      return result
    },
    createCoinBurst(event) {
      // 클릭 위치 가져오기
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      // 8개의 동전 생성
      const coinCount = 8
      for (let i = 0; i < coinCount; i++) {
        const angle = (Math.PI * 2 * i) / coinCount
        const velocity = 50 + Math.random() * 30
        const vx = Math.cos(angle) * velocity
        const vy = Math.sin(angle) * velocity - 20 // 위쪽으로 약간 편향

        const coin = {
          id: this.coinIdCounter++,
          x: x,
          y: y,
          vx: vx,
          vy: vy,
          opacity: 1
        }

        this.coins.push(coin)

        // 애니메이션 후 제거
        setTimeout(() => {
          const index = this.coins.findIndex(c => c.id === coin.id)
          if (index > -1) {
            this.coins.splice(index, 1)
          }
        }, 800)
      }
    },
    onReject() {
      this.dialog = false
    },
    showWalletDialog() {
      this.dialog = true
    },
    async onRetryClick() {
      // 재도전 확인
      if (!confirm('10 UMCP를 차감하고 다시 도전하시겠습니까?')) {
        return
      }

      try {
        // 리워드 포인트가 충분한지 확인
        if (this.rewardTotal < 10) {
          this.notify('리워드 포인트가 부족합니다. (필요: 10 UMCP)')
          return
        }

        this.notify('재도전 시작...')

        // 백엔드에 재도전 요청 (10 UMCP 차감)
        // TODO: 실제 백엔드 API 호출 구현 필요
        // await retry_game({ wallet_address: this.account, cost: 10 })

        // 게임 초기화
        this.count = 0
        this.clickRewards = []
        this.totalClickReward = 0
        
        // 새로운 게임 시작: 5.0~9.5 사이 총 리워드 결정
        this._total = this.generateTotalReward()
        this._total +=4.0;
        // 총 리워드를 10개로 무작위 분리
        this._totalAry = this.splitRewardRandomly(this._total, this.maxClicks)
        
        console.log('재도전 시작 - 총 리워드:', this._total)
        console.log('분리된 리워드:', this._totalAry)

        // 리워드 요약 갱신
        if (this.account) {
          try {
            await this.call_rewards(this.account)
          } catch (e) {
            console.error('refresh rewards after retry failed', e)
          }
        }

        this.notify('재도전이 시작되었습니다! 다시 클릭해주세요.')
      } catch (err) {
        console.error('onRetryClick error', err)
        this.notify('재도전 중 오류가 발생했습니다.')
      }
    },
    async onRewardClick() {
      // 모달창 닫기
      this.rewardDialog = false
      
      // 10회 클릭이 완료되지 않았다면 리워드 지급 불가
      if (this.count < this.maxClicks) {
        this.notify('10회 클릭을 완료해주세요!')
        return
      }

      // notify first (uses notify helper)
      this.notify('리워드 지급 요청중...')

      // call backend reward endpoint if available
      try {
        if (typeof reward_claim === 'function') {
          const payload = {
            wallet_address: this.account,
            click_count: this.count,
            wallet_type: this.walletType,
            reward_amount: this.totalClickReward, // 총 리워드 금액 전달
            click_rewards: this.clickRewards, // 각 클릭의 리워드 배열
          }
          const res = await reward_claim(payload)
          // axios instance returns response object
          const data = res && res.data ? res.data : res
          if (data && data.status) {
            this.notify(`리워드 ${this.totalClickReward.toFixed(1)} UMCP 지급되었습니다`)
            // 지급 후 리워드 요약 즉시 갱신
            if (this.account) {
              try {
                await this.call_rewards(this.account)
              } catch (e) {
                console.error('refresh rewards after claim failed', e)
              }
            }
          } else {
            const errMsg = (data && data.error) || '리워드 지급 요청에 실패했습니다.'
            this.notify(errMsg)
          }
        } else {
          // fallback notify if reward_claim missing
          this.notify(`리워드 ${this.totalClickReward.toFixed(1)} UMCP 지급되었습니다`)
        }
      } catch (err) {
        console.error('onRewardClick error', err)
        this.notify('리워드 지급 중 오류가 발생했습니다.')
      } finally {
        // 게임 초기화
        this.count = 0
        this.clickRewards = []
        this.totalClickReward = 0
        // this._total = 0
        // this._totalAry = []
        this._total = this.generateTotalReward()        
        this._totalAry = this.splitRewardRandomly(this._total, this.maxClicks)        
        

      }
    },
    async connectWallet(wallet) {
      this.errorMessage = ''
      try {
        let provider = null

        if (wallet === 'metamask') {
          // MetaMask 연결 - isMetaMask 플래그로 식별
          if (!window.ethereum || !window.ethereum.isMetaMask) {
            this.errorMessage = 'MetaMask가 설치되지 않았습니다.'
            return
          }
          provider = window.ethereum
          await provider.request({ method: 'eth_requestAccounts' })
        } else if (wallet === 'phantom') {
          // Phantom 연결 - window.phantom.ethereum 직접 사용
          if (!window.phantom || !window.phantom.ethereum) {
            this.errorMessage = 'Phantom이 설치되지 않았습니다.'
            return
          }
          provider = window.phantom.ethereum
          await provider.request({ method: 'eth_requestAccounts' })
        } else if (wallet === 'tokenpocket') {
          // TokenPocket 연결 - window.tokenPocket 직접 사용
          if (!window.tokenPocket || !window.tokenPocket.ethereum) {
            this.errorMessage = 'TokenPocket이 설치되지 않았습니다.'
            return
          }
          provider = window.tokenPocket.ethereum
          await provider.request({ method: 'eth_requestAccounts' })
        } else {
          this.errorMessage = '지원하지 않는 지갑입니다.'
          return
        }

        // Web3 인스턴스 생성
        const web3 = new Web3(provider)
        
        // 계정 가져오기
        const accounts = await web3.eth.getAccounts()
        if (accounts.length === 0) {
          this.errorMessage = '지갑 계정을 찾을 수 없습니다.'
          return
        }

        this.account = accounts[0]
        this.provider = provider
        this.web3 = web3
        this.walletType = wallet
        this.dialog = false
        // this.call_rewards(this.account) ;

      } catch (err) {
        console.error(err)
        this.errorMessage = err.message || '지갑 연결 중 오류가 발생했습니다.'
      }
    },
    // 사용자가 직접 입력한 개인 지갑 주소로 연결 (서명/전송은 불가)
    connectManualAddress() {
      this.errorMessage = ''
      const addr = (this.manualAddress || '').trim()
      if (!addr || !addr.startsWith('0x') || addr.length !== 42) {
        this.errorMessage = '올바른 0x 지갑 주소를 입력해 주세요.'
        return
      }

      // 프로바이더가 없는 상태로 주소만 설정
      this.account = addr
      /** 강제 지정 & 참여 */
      this.provider = true ; 
      this.web3 = {type: 'manual' , wallet : addr }
      this.walletType = 'manual'
      // this.provider = null
      // this.web3 = null      
      this.dialog = false

      // 안내: 주소만 설정했을 경우 서명/전송 불가
      this.errorMessage = '주소가 설정되었습니다. 서명/전송은 브라우저 지갑 연결이 필요합니다.'
    },
    async signMessage() {
      try {
        this.errorMessage = ''
        this.lastSignature = ''
        if (!this.account || !this.provider) {
          this.errorMessage = '먼저 지갑을 연결해 주세요.'
          return
        }
        
        this.call_rewards(  this.account ) ;

        // 게임 시작: 5.0~9.5 사이 총 리워드 결정
        this._total = this.generateTotalReward()
        // 총 리워드를 10개로 무작위 분리
        this._totalAry = this.splitRewardRandomly(this._total, this.maxClicks)
        
        console.log('게임 시작 - 총 리워드:', this._total)
        console.log('분리된 리워드:', this._totalAry)
        console.log('배열 합계 검증:', this._totalAry.reduce((a, b) => a + b, 0).toFixed(1))

        // 개인 지갑 주소 사용인 경우 (수동 입력, provider가 true)
        if (this.provider === true) {
          // 지갑 주소를 직접 lastSignature에 저장
          this.lastSignature = this.account
          return
        }

        // 실제 지갑 연결된 경우 (provider에 request 메서드가 있음)
        const message = this.messageToSign || 'Default sign message'
        
        // 메시지 서명 (EIP-191 표준)
        const signature = await this.provider.request({
          method: 'personal_sign',
          params: [message, this.account]
        })
        this.lastSignature = signature
      } catch (err) {
        console.error(err)
        this.errorMessage = '메시지 서명에 실패했습니다.'
      }
    },
    // 이더 전송 기능이 제거되었습니다.
  }
}
</script>

<style scoped>
.fill-height {
  
  background: #f2f2f2;
  padding-top: 20px;
  padding-bottom: 80px;
}

.coin-particle {
  position: absolute;
  font-size: 24px;
  pointer-events: none;
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  animation: coin-fade 0.8s ease-out forwards;
  z-index: 1000;
}

@keyframes coin-fade {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: scale(0.3) rotate(360deg);
  }
}
</style>
